import React, { useEffect, useState } from "react";
import {
  MapPin,
  Phone,
  Trophy,
  ShieldCheck,
  UserRound,
} from "lucide-react";

const apiUrl = import.meta.env.VITE_API_URL;

function AllPlayer() {
  const [userData, setUserData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dataLength, setDataLength] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${apiUrl}/api/players`);

        if (!response.ok) {
          throw new Error("Failed to fetch players");
        }

        let data = await response.json();

        setDataLength(data.length);

        // Latest registered players first
        data = data.sort(
          (a, b) =>
            new Date(b.created_at) - new Date(a.created_at)
        );

        setUserData(data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const getRole = (role) => {
    switch (Number(role)) {
      case 1:
        return "Batsman";
      case 2:
        return "Wicket-Keeper";
      case 3:
        return "Batsman-Allrounder";
      case 4:
        return "Bowler-Allrounder";
      case 5:
        return "Bowler";
      default:
        return "Unknown";
    }
  };

  const formatAddress = (address) => {
    if (!address) return "N/A";

    return address
      .split(" ")
      .map(
        (word) =>
          word.charAt(0).toUpperCase() +
          word.slice(1).toLowerCase()
      )
      .join(" ");
  };

  const maskPhone = (phone) => {
    return String(phone || "").replace(
      /(\d{2})\d{4}(\d{4})/,
      "$1****$2"
    );
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-[#100404] via-[#250707] to-black">
        <div className="text-center">
          <div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-white/10 border-t-orange-500" />

          <p className="mt-4 text-sm font-semibold text-gray-400">
            Loading Players...
          </p>
        </div>
      </div>
    );
  }

  return (
    <section className="min-h-screen bg-gradient-to-br from-[#100404] via-[#250707] to-black px-4 pb-12 pt-8 text-white sm:px-6 md:pt-12">

      {/* ========================================= */}
      {/* HEADER */}
      {/* ========================================= */}

      <div className="mx-auto mb-10 max-w-7xl text-center">

        <div className="mb-3 flex items-center justify-center gap-2">
          <Trophy className="h-5 w-5 text-orange-400" />

          <span className="text-xs font-bold uppercase tracking-[0.3em] text-orange-400 sm:text-sm">
            BPL Season 2
          </span>

          <Trophy className="h-5 w-5 text-orange-400" />
        </div>

        <h1 className="text-3xl font-black tracking-tight sm:text-4xl md:text-5xl">
          Player Squad
        </h1>

        <p className="mt-2 text-sm text-gray-400 sm:text-base">
          Badgaw Premier League
        </p>

        {/* Player Count */}
        <div className="mx-auto mt-5 inline-flex items-center gap-2 rounded-full border border-orange-500/20 bg-orange-500/10 px-4 py-2 text-sm font-semibold text-orange-300">
          <UserRound className="h-4 w-4" />

          {dataLength} Registered Players
        </div>
      </div>

      {/* ========================================= */}
      {/* PLAYER CARDS */}
      {/* ========================================= */}

      <div className="mx-auto grid max-w-7xl gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">

        {userData.map((player) => (
          <div
            key={player.id}
            className="group relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.05] shadow-xl backdrop-blur-xl transition-all duration-300 hover:-translate-y-1 hover:border-orange-500/30 hover:shadow-orange-950/30"
          >

            {/* Top Gradient */}
            <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-br from-red-700/40 via-orange-600/20 to-transparent" />

            {/* Decorative Circle */}
            <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-orange-500/10 blur-2xl transition group-hover:bg-orange-500/20" />

            <div className="relative p-5">

              {/* ================================= */}
              {/* PLAYER PHOTO */}
              {/* ================================= */}

              <div className="flex justify-center">

                <div className="relative">

                  {/* Orange ring */}
                  <div className="rounded-full bg-gradient-to-br from-red-600 via-orange-500 to-yellow-400 p-[3px] shadow-lg shadow-red-950/40">

                    <div className="rounded-full bg-[#160606] p-[3px]">

                      {player.photo ? (
                        <img
                          src={player.photo}
                          alt={player.name}
                          className="h-24 w-24 rounded-full object-cover sm:h-28 sm:w-28"
                        />
                      ) : (
                        <div className="flex h-24 w-24 items-center justify-center rounded-full bg-gray-900 sm:h-28 sm:w-28">
                          <UserRound className="h-10 w-10 text-gray-600" />
                        </div>
                      )}

                    </div>
                  </div>

                  {/* Approved Badge */}
                  {Number(player.status) === 1 && (
                    <div className="absolute -bottom-1 -right-1 flex h-8 w-8 items-center justify-center rounded-full border-2 border-[#160606] bg-green-600 shadow-lg">
                      <ShieldCheck className="h-4 w-4 text-white" />
                    </div>
                  )}

                </div>
              </div>

              {/* ================================= */}
              {/* PLAYER NAME */}
              {/* ================================= */}

              <div className="mt-5 text-center">

                <h2 className="text-xl font-black text-white">
                  {player.name}

                  {player?.islocal !== 1 && (
                    <span className="ml-1.5" title="Non-local Player">
                      ✈️
                    </span>
                  )}
                </h2>

                {/* Role */}
                <div className="mt-2 inline-flex rounded-full border border-orange-500/20 bg-orange-500/10 px-3 py-1">
                  <span className="text-xs font-bold text-orange-300">
                    {getRole(player.role)}
                  </span>
                </div>

              </div>

              {/* ================================= */}
              {/* PLAYER DETAILS */}
              {/* ================================= */}

              <div className="mt-5 space-y-2.5">

                {/* Address */}
                <div className="flex items-center gap-3 rounded-xl border border-white/5 bg-black/20 px-3 py-2.5">
                  <MapPin className="h-4 w-4 shrink-0 text-orange-400" />

                  <span className="truncate text-xs text-gray-400">
                    {formatAddress(player.address)}
                  </span>
                </div>

                {/* Phone */}
                <div className="flex items-center gap-3 rounded-xl border border-white/5 bg-black/20 px-3 py-2.5">
                  <Phone className="h-4 w-4 shrink-0 text-orange-400" />

                  <a
                    href={`tel:${player.phone}`}
                    className="text-xs font-medium text-gray-400 transition hover:text-white"
                  >
                    {maskPhone(player.phone)}
                  </a>
                </div>

              </div>

              {/* ================================= */}
              {/* BOTTOM */}
              {/* ================================= */}

              <div className="mt-5 flex items-center justify-between border-t border-white/10 pt-4">

                {/* Player ID */}
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-600">
                    Player ID
                  </p>

                  <p className="mt-0.5 text-sm font-black text-white">
                    #{player.p_id || player.id}
                  </p>
                </div>

                {/* Status */}
                {Number(player.status) === 1 ? (
                  <div className="flex items-center gap-1.5 rounded-full border border-green-500/20 bg-green-500/10 px-3 py-1.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-green-400" />

                    <span className="text-[10px] font-bold uppercase tracking-wide text-green-400">
                      Approved
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5 rounded-full border border-yellow-500/20 bg-yellow-500/10 px-3 py-1.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-yellow-400" />

                    <span className="text-[10px] font-bold uppercase tracking-wide text-yellow-400">
                      Pending
                    </span>
                  </div>
                )}

              </div>

            </div>
          </div>
        ))}

      </div>

      {/* Empty */}
      {userData.length === 0 && (
        <div className="mx-auto max-w-md rounded-2xl border border-white/10 bg-white/[0.05] p-10 text-center">
          <UserRound className="mx-auto h-10 w-10 text-gray-600" />

          <p className="mt-3 text-sm text-gray-500">
            No players registered yet.
          </p>
        </div>
      )}

    </section>
  );
}

export default AllPlayer;